import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { useTimer } from '../contexts/TimerContext';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, Square, Clock, Calendar, FileText, Check, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { Button } from '../components/ui/button';
import AdminTimeEntryEditDialog from '../components/AdminTimeEntryEditDialog';
import { TimeEntryDeleteDialog } from '../components/TimeEntryDeleteDialog';
import { TimeEntryActions } from '../components/TimeEntryActions';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TimerPage = () => {
  const { activeEntry, refreshActiveEntry, isRunning, isPaused, elapsedTime, formatTime, startTimer, pauseTimer, resumeTimer, stopTimer } = useTimer();
  const { getAuthHeader } = useAuth();
  const { t, i18n } = useTranslation();
  const [recentEntries, setRecentEntries] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);
  const [deletingEntry, setDeletingEntry] = useState(null);
  const [todaySeconds, setTodaySeconds] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [stopDialogOpen, setStopDialogOpen] = useState(false);
  const [activityDescription, setActivityDescription] = useState('');
  const [isStopping, setIsStopping] = useState(false);
  const [stoppedDuration, setStoppedDuration] = useState(0);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualDate, setManualDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [manualHours, setManualHours] = useState('');
  const [manualMinutes, setManualMinutes] = useState('');
  const [manualDesc, setManualDesc] = useState('');
  const [manualSaving, setManualSaving] = useState(false);

  const fetchRecentEntries = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/time-entries?own_only=true&limit=20`, {
        headers: getAuthHeader()
      });
      setRecentEntries(response.data);
      setHasMore(response.data.length === 20);
      const total = await axios.get(`${API_URL}/time-entries/daily-total`, {
        params: { date: format(new Date(), 'yyyy-MM-dd') }, headers: getAuthHeader(),
      });
      setTodaySeconds(total.data.seconds);
    } catch (error) {
      console.error('Failed to fetch recent entries:', error);
      toast.error(t('timeCorrection.loadError'));
    }
  }, [getAuthHeader, t]);

  useEffect(() => {
    fetchRecentEntries();
  }, [isRunning, fetchRecentEntries]);

  const refreshEntries = () => {
    refreshActiveEntry();
    fetchRecentEntries();
  };

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const response = await axios.get(`${API_URL}/time-entries`, {
        params: { own_only: true, limit: 20, skip: recentEntries.length }, headers: getAuthHeader(),
      });
      setRecentEntries(previous => [...previous, ...response.data.filter(item => !previous.some(p => p.id === item.id))]);
      setHasMore(response.data.length === 20);
    } catch (error) {
      toast.error(t('timeCorrection.loadError'));
    } finally {
      setLoadingMore(false);
    }
  };

  const handleStart = async () => {
    try {
      await startTimer();
      toast.success(t('timer.started'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('timer.errorStart'));
    }
  };

  const handlePause = async () => {
    try {
      await pauseTimer();
      toast.success(t('timer.pausedMsg'));
    } catch (error) {
      toast.error(t('timer.errorPause'));
    }
  };

  const handleResume = async () => {
    try {
      await resumeTimer();
      toast.success(t('timer.resumed'));
    } catch (error) {
      toast.error(t('timer.errorResume'));
    }
  };

  const handleStopClick = () => {
    // Store the current elapsed time before opening dialog
    setStoppedDuration(elapsedTime);
    setActivityDescription('');
    setStopDialogOpen(true);
  };

  const handleConfirmStop = async () => {
    setIsStopping(true);
    try {
      await stopTimer(activityDescription.trim() || null);
      toast.success(t('timer.stoppedMsg'));
      setStopDialogOpen(false);
      setActivityDescription('');
      fetchRecentEntries();
    } catch (error) {
      toast.error(t('timer.errorStop'));
    } finally {
      setIsStopping(false);
    }
  };

  const handleManualSave = async () => {
    const h = parseInt(manualHours || '0', 10) || 0;
    const m = parseInt(manualMinutes || '0', 10) || 0;
    if (h * 60 + m <= 0) {
      toast.error(t('timer.manualDurationRequired'));
      return;
    }
    setManualSaving(true);
    try {
      await axios.post(`${API_URL}/time-entries/manual`, {
        date: manualDate,
        hours: h,
        minutes: m,
        description: manualDesc.trim() || null,
      }, { headers: getAuthHeader() });
      toast.success(t('timer.manualSuccess'));
      setManualOpen(false);
      setManualHours('');
      setManualMinutes('');
      setManualDesc('');
      setManualDate(format(new Date(), 'yyyy-MM-dd'));
      fetchRecentEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('timer.manualError'));
    } finally {
      setManualSaving(false);
    }
  };

  const handleSkipDescription = async () => {
    setIsStopping(true);
    try {
      await stopTimer(null);
      toast.success(t('timer.stoppedMsg'));
      setStopDialogOpen(false);
      setActivityDescription('');
      fetchRecentEntries();
    } catch (error) {
      toast.error(t('timer.errorStop'));
    } finally {
      setIsStopping(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-8" data-testid="timer-page">
        <div className="text-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('timer.title')}
          </h1>
          <p className="text-zinc-600">{t('timer.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Timer Card */}
          <div className="lg:col-span-2">
            <motion.div
              className={`bg-white p-5 sm:p-12 rounded-3xl border-2 shadow-xl ${
                isRunning ? 'border-[#D93025] shadow-[#D93025]/20' : 'border-zinc-200'
              }`}
              data-testid="timer-card"
            >
              <div className="text-center mb-10">
                <motion.div
                  animate={isRunning && !isPaused ? { scale: [1, 1.02, 1] } : {}}
                  transition={{ repeat: Infinity, duration: 2 }}
                  className="text-4xl sm:text-6xl font-mono font-bold text-[#D93025] mb-6 break-all"
                  style={{ fontFamily: 'Space Mono, monospace' }}
                  data-testid="timer-display"
                >
                  {formatTime(elapsedTime)}
                </motion.div>
                <div className="flex items-center justify-center gap-2">
                  {isRunning && !isPaused && (
                    <motion.div
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ repeat: Infinity, duration: 1.5 }}
                      className="w-3 h-3 bg-[#D93025] rounded-full"
                    />
                  )}
                  <p className="text-zinc-600 text-lg">
                    {!isRunning ? t('timer.stopped') : isPaused ? t('timer.paused') : t('timer.running')}
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-center gap-3">
                {!isRunning ? (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleStart}
                    data-testid="start-timer-button"
                    className="bg-gradient-to-r from-[#D93025] to-[#B91C1C] hover:from-[#B91C1C] hover:to-[#991B1B] text-white rounded-full px-10 py-4 text-lg font-bold shadow-lg flex items-center justify-center gap-2 transition-all"
                  >
                    <Play className="w-5 h-5" fill="white" />
                    {t('timer.start')}
                  </motion.button>
                ) : (
                  <>
                    {!isPaused ? (
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handlePause}
                        data-testid="pause-timer-button"
                        className="bg-gradient-to-r from-[#F59E0B] to-[#D97706] hover:from-[#D97706] hover:to-[#B45309] text-white rounded-full px-8 py-4 text-base font-bold shadow-lg flex items-center justify-center gap-2 transition-all"
                      >
                        <Pause className="w-5 h-5" fill="white" />
                        {t('timer.pause')}
                      </motion.button>
                    ) : (
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleResume}
                        data-testid="resume-timer-button"
                        className="bg-gradient-to-r from-[#10B981] to-[#059669] hover:from-[#059669] hover:to-[#047857] text-white rounded-full px-8 py-4 text-base font-bold shadow-lg flex items-center justify-center gap-2 transition-all"
                      >
                        <Play className="w-5 h-5" fill="white" />
                        {t('timer.resume')}
                      </motion.button>
                    )}
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleStopClick}
                      data-testid="stop-timer-button"
                      className="bg-white text-[#D93025] border-2 border-[#D93025] rounded-full px-8 py-4 text-base font-bold shadow-lg hover:bg-gradient-to-br hover:from-[#FEE2E2] hover:to-white transition-all flex items-center justify-center gap-2"
                    >
                      <Square className="w-5 h-5" fill="currentColor" />
                      {t('timer.stop')}
                    </motion.button>
                  </>
                )}
              </div>
            </motion.div>
          </div>

          {/* Today's Summary */}
          <div className="bg-gradient-to-br from-white to-zinc-50 p-6 rounded-3xl border-2 border-zinc-200 shadow-lg">
            <h3 className="text-lg font-bold text-zinc-900 mb-6 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#D93025]" />
              {t('timer.today')}
            </h3>
            <div className="space-y-4">
              <div className="p-5 bg-gradient-to-br from-[#D1FAE5] to-[#A7F3D0] rounded-2xl">
                <div className="text-sm text-[#047857] mb-2 font-medium">{t('timer.currentSession')}</div>
                <div className="text-3xl font-bold text-[#047857] break-all" data-testid="timer-current-session">
                  {isRunning ? formatTime(elapsedTime) : '00:00:00'}
                </div>
              </div>
              <div className="p-5 bg-gradient-to-br from-zinc-100 to-zinc-50 rounded-2xl">
                <div className="text-sm text-zinc-600 mb-2 font-medium">{t('timer.totalToday')}</div>
                <div className="text-3xl font-bold text-zinc-900 break-all" data-testid="timer-total-today">
                  {formatTime(todaySeconds + (isRunning && activeEntry?.date === format(new Date(), 'yyyy-MM-dd') ? elapsedTime : 0))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Entries */}
        <div className="bg-white p-6 rounded-2xl border border-zinc-200">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <h2 className="text-xl font-bold text-zinc-900 flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#D93025]" />
              {t('timer.recentEntries')}
            </h2>
            <Button
              variant="outline"
              onClick={() => setManualOpen(true)}
              data-testid="manual-entry-button"
              className="rounded-full h-9 border-[#D93025]/30 text-[#D93025] hover:bg-[#FEE2E2]"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              {t('timer.manualEntry')}
            </Button>
          </div>
          <div className="space-y-2" data-testid="recent-entries">
            {activeEntry && (
              <div className="p-4 rounded-xl border border-amber-200 bg-amber-50 flex flex-wrap items-center justify-between gap-3" data-testid="active-time-entry">
                <div className="min-w-0">
                  <p className="font-semibold text-amber-900">{t('timeCorrection.activeEntry')}</p>
                  <p className="text-sm text-amber-800">{format(new Date(activeEntry.start_time), 'dd.MM.yyyy HH:mm')}</p>
                </div>
                <TimeEntryActions entry={activeEntry} onEdit={setEditingEntry} onDelete={setDeletingEntry} />
              </div>
            )}
            {recentEntries.filter(e => e.end_time).length === 0 ? (
              <p className="text-zinc-600 text-center py-8">{t('timer.noEntries')}</p>
            ) : (
              recentEntries.filter(e => e.end_time).map((entry, index) => (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-4 rounded-xl border border-zinc-200 hover:bg-zinc-50 transition-colors"
                  data-testid={`entry-${index}`}
                >
                  <div className="grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-3">
                    <div className="col-span-2 sm:col-span-1 min-w-0">
                      <div className="font-medium text-zinc-900">
                        {format(new Date(entry.start_time), 'd. MMMM yyyy', { locale: i18n.language === 'de' ? de : undefined })}
                      </div>
                      <div className="text-sm text-zinc-600">
                        {format(new Date(entry.start_time), 'HH:mm')} - {format(new Date(entry.end_time), 'HH:mm')}
                      </div>
                      {entry.description && (
                        <div className="mt-2 p-2 bg-zinc-50 rounded-lg text-sm text-zinc-700 flex items-start gap-2">
                          <FileText className="w-4 h-4 text-zinc-400 flex-shrink-0 mt-0.5" />
                          <span className="line-clamp-2 break-all">{entry.description}</span>
                        </div>
                      )}
                    </div>
                    <div className="text-xl font-bold text-[#D93025] shrink-0" data-testid={`entry-duration-${entry.id}`}>
                      {formatTime(entry.duration)}
                    </div>
                    <TimeEntryActions entry={entry} onEdit={setEditingEntry} onDelete={setDeletingEntry} />
                  </div>
                </motion.div>
              ))
            )}
          </div>
          {hasMore && <Button data-testid="load-more-time-entries" variant="outline" className="mt-4 w-full" disabled={loadingMore} onClick={loadMore}>
            {loadingMore ? t('common.loading') : t('timeCorrection.loadMore')}
          </Button>}
        </div>

        <div className="mt-8 bg-white p-6 rounded-2xl border border-zinc-200">
          <h3 className="font-medium text-zinc-900 mb-2">{t('timer.howItWorks')}</h3>
          <ul className="text-sm text-zinc-600 space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-[#D93025] font-bold">1.</span>
              <span>{t('timer.step1')}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#D93025] font-bold">2.</span>
              <span>{t('timer.step2')}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#D93025] font-bold">3.</span>
              <span>{t('timer.step3')}</span>
            </li>
          </ul>
        </div>
      </div>

      <AdminTimeEntryEditDialog open={!!editingEntry} entry={editingEntry}
        onOpenChange={open => { if (!open) setEditingEntry(null); }} onSaved={refreshEntries} />
      <TimeEntryDeleteDialog entry={deletingEntry} onClose={() => setDeletingEntry(null)} onDeleted={refreshEntries} />

      {/* Stop Timer Dialog - Activity Description */}
      <Dialog open={stopDialogOpen} onOpenChange={setStopDialogOpen}>
        <DialogContent data-testid="stop-timer-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#D93025] to-[#B91C1C] flex items-center justify-center text-white">
                <Clock className="w-6 h-6" />
              </div>
              <div>
                <div className="text-lg font-bold">{t('timer.sessionComplete')}</div>
                <div className="text-sm text-zinc-500 font-normal">{t('timer.duration')}: {formatTime(stoppedDuration)}</div>
              </div>
            </DialogTitle>
            <DialogDescription className="sr-only">{t('timer.whatDidYouDo')}</DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <label className="block text-sm font-medium text-zinc-700 mb-2">
              {t('timer.whatDidYouDo')}
            </label>
            <Textarea
              value={activityDescription}
              onChange={(e) => setActivityDescription(e.target.value)}
              placeholder={t('timer.activityPlaceholder')}
              className="min-h-[120px] resize-none"
              data-testid="activity-description-input"
              autoFocus
            />
            <p className="text-xs text-zinc-500 mt-2">{t('timer.activityHint')}</p>
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              variant="ghost" 
              onClick={handleSkipDescription}
              disabled={isStopping}
              className="rounded-full px-6"
            >
              {t('timer.skipAndSave')}
            </Button>
            <Button 
              onClick={handleConfirmStop}
              disabled={isStopping}
              className="bg-[#D93025] hover:bg-[#B91C1C] text-white rounded-full px-6"
            >
              <Check className="w-4 h-4 mr-2" />
              {t('timer.saveActivity')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Time Entry Dialog */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent data-testid="manual-time-entry-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#D93025] to-[#B91C1C] flex items-center justify-center text-white">
                <Plus className="w-6 h-6" />
              </div>
              <div>
                <div className="text-lg font-bold">{t('timer.manualTitle')}</div>
                <div className="text-sm text-zinc-500 font-normal">{t('timer.manualSubtitle')}</div>
              </div>
            </DialogTitle>
            <DialogDescription className="sr-only">{t('timer.manualSubtitle')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-medium">{t('timer.manualDate')}</Label>
              <Input
                type="date"
                value={manualDate}
                max={format(new Date(), 'yyyy-MM-dd')}
                onChange={(e) => setManualDate(e.target.value)}
                className="mt-1.5 h-11"
                data-testid="manual-date-input"
              />
            </div>

            <div>
              <Label className="text-sm font-medium">{t('timer.manualDurationLabel')}</Label>
              <div className="grid grid-cols-2 gap-3 mt-1.5">
                <div className="relative">
                  <Input
                    type="number"
                    min="0"
                    max="24"
                    inputMode="numeric"
                    value={manualHours}
                    onChange={(e) => setManualHours(e.target.value)}
                    placeholder="0"
                    className="h-11 pr-10"
                    data-testid="manual-hours-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">{t('timer.manualHours')}</span>
                </div>
                <div className="relative">
                  <Input
                    type="number"
                    min="0"
                    max="59"
                    inputMode="numeric"
                    value={manualMinutes}
                    onChange={(e) => setManualMinutes(e.target.value)}
                    placeholder="0"
                    className="h-11 pr-12"
                    data-testid="manual-minutes-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">{t('timer.manualMinutes')}</span>
                </div>
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">{t('timer.whatDidYouDo')}</Label>
              <Textarea
                value={manualDesc}
                onChange={(e) => setManualDesc(e.target.value)}
                placeholder={t('timer.manualDescPlaceholder')}
                className="mt-1.5 min-h-[90px] resize-none"
                data-testid="manual-description-input"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setManualOpen(false)}
              disabled={manualSaving}
              className="rounded-full px-6"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleManualSave}
              disabled={manualSaving}
              data-testid="manual-save-button"
              className="bg-[#D93025] hover:bg-[#B91C1C] text-white rounded-full px-6"
            >
              <Check className="w-4 h-4 mr-2" />
              {t('timer.manualSave')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default TimerPage;
